import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, PageLoader, Btn } from "@/lib/ui";
import { Plug, Zap, CheckCircle2 } from "lucide-react";

const STATE_TONE = {
  ACTIVE: "ok", CONNECTED: "ok", READY_FOR_CONNECTION: "warn",
  CONNECT_VIA_OAUTH: "info", AVAILABLE: "muted",
};
const STATE_LABEL = {
  ACTIVE: "Active", CONNECTED: "Connected", READY_FOR_CONNECTION: "Ready",
  CONNECT_VIA_OAUTH: "OAuth", AVAILABLE: "Available",
};

export default function Integrations() {
  const api = useApi();
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({ queryKey: ["integrations"], queryFn: () => api.get("/integrations") });

  const test = useMutation({
    mutationFn: (key) => api.post(`/integrations/${key}/test`),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["integrations"] }); toast.success(`${r.key} connection OK`); },
    onError: (e) => { qc.invalidateQueries({ queryKey: ["integrations"] }); toast.error(e.message); },
  });

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader kicker="System" title="Integration Centre" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => (
          <Panel key={it.key} className="p-4 flex flex-col" data-testid={`integration-${it.key}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Plug className="h-4 w-4 text-muted-foreground" />
                <span className="font-head font-bold">{it.name}</span>
              </div>
              <StatusBadge tone={STATE_TONE[it.state] || "muted"} dot={it.state === "ACTIVE" || it.state === "CONNECTED"}>
                {STATE_LABEL[it.state] || it.state}
              </StatusBadge>
            </div>
            <p className="text-xs text-muted-foreground mt-2 flex-1">{it.description}</p>
            <div className="mt-3 space-y-1 text-[11px] font-mono text-muted-foreground">
              <div>env · {it.environment}</div>
              <div className="flex items-center gap-1">
                {it.last_success && <CheckCircle2 className="h-3 w-3 text-ok" />}
                last ok · {it.last_success ? new Date(it.last_success).toLocaleString() : "—"}
              </div>
              {it.last_error && <div className="text-err truncate">err · {it.last_error}</div>}
            </div>
            <Btn size="sm" variant="outline" className="mt-3" onClick={() => test.mutate(it.key)}
              disabled={test.isPending} data-testid={`test-integration-${it.key}`}>
              <Zap className="h-3.5 w-3.5" /> Test Connection
            </Btn>
          </Panel>
        ))}
      </div>
    </div>
  );
}
